// ===================================================================
// Atendimento especial (modo prova) — fila tipo isenção / análise curricular (sem notas)
// ===================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAdminProcess } from '../../contexts/AdminProcessContext';
import { parseAdminSelectedProcessId } from '../../utils/adminProcessId';
import {
  initialAdminTableSort,
  nextAdminTableSort,
  sortColumnIndicator,
  sortSarRowsForDisplay,
} from '../../utils/adminTableSort';
import {
  getButtonStyle,
  getPaymentStatusLabel,
  getStatusBadgeStyle,
  getAssignmentStatusLabel,
  getSpecialAttendanceStatusLabel,
  ACTION_ICONS,
} from '../../constants/statusTranslations';

import { INGRESSA_STORAGE_KEYS } from '../../utils/ingressaBrand';

const STORAGE_KEY = INGRESSA_STORAGE_KEYS.filtersSpecialAttendance;

/** Motivo devolução AT esp.: lista SAR usa `return_reason`; GET /applications usa `special_attendance_return_reason`. */
const sarReturnReasonText = (r) =>
  String((r && (r.return_reason || r.special_attendance_return_reason)) || '').trim() || null;

const getPaymentStatusForAnalysis = (application) => {
  if (!application) return 'pending';
  if (application.payment_status === 'paid') return 'paid';
  if (application.exemption_status) {
    switch (application.exemption_status) {
      case 'approved':
        return 'exempted';
      case 'rejected':
        return 'exemption_rejected';
      case 'returned':
        return 'exemption_returned';
      case 'appealed':
        return 'exemption_appealed';
      case 'appeal_approved':
        return 'exemption_appeal_approved';
      case 'appeal_rejected':
        return 'exemption_appeal_rejected';
      case 'pending': {
        const hasExemptionAnalyst = application.exemption_analyst_id || application.exemption_analyst_name;
        if (!hasExemptionAnalyst) return 'pending_exemption_not_assigned';
        return application.exemption_request_status === 'analyzing'
          ? 'pending_exemption'
          : 'pending_exemption_assigned';
      }
      default:
        break;
    }
  }
  if (application.payment_status) return application.payment_status;
  return 'pending';
};

const sarDecisionBadgeKey = (r) => {
  const s = (r.status || '').toLowerCase();
  if (s === 'approved') return 'approved';
  if (s === 'rejected') return 'rejected';
  if (s === 'appealed') return 'exemption_appealed';
  if (s === 'appeal_approved') return 'exemption_appeal_approved';
  if (s === 'appeal_rejected') return 'exemption_appeal_rejected';
  if (sarReturnReasonText(r) && !r.special_attendance_analyst_id) return 'returned';
  return 'pending';
};

const sarDecisionLabel = (r) => {
  if (sarReturnReasonText(r) && !r.special_attendance_analyst_id) {
    return getSpecialAttendanceStatusLabel('returned');
  }
  return getSpecialAttendanceStatusLabel(r.status || 'pending');
};

export const SpecialAttendanceAnalysis = React.memo(({ user }) => {
  const { selectedProcessId, setSelectedProcessId, processes } = useAdminProcess();
  const [rows, setRows] = useState([]);
  const [applications, setApplications] = useState([]);
  const [analysts, setAnalysts] = useState([]);
  const [users, setUsers] = useState([]);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPayment, setFilterPayment] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) return JSON.parse(s).filterPayment || '';
    } catch (e) {}
    return '';
  });
  const [filterCandidate, setFilterCandidate] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) return String(JSON.parse(s).filterCandidate || '');
    } catch (e) {}
    return '';
  });
  const [filterAnalyst, setFilterAnalyst] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) return String(JSON.parse(s).filterAnalyst || '');
    } catch (e) {}
    return '';
  });
  const [filterCourse, setFilterCourse] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) return String(JSON.parse(s).filterCourse || '');
    } catch (e) {}
    return '';
  });
  const [filterCampus, setFilterCampus] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) return String(JSON.parse(s).filterCampus || '');
    } catch (e) {}
    return '';
  });
  const [filterQuota, setFilterQuota] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) return String(JSON.parse(s).filterQuota || '');
    } catch (e) {}
    return '';
  });
  const [showModal, setShowModal] = useState(false);
  const [analysisModalMode, setAnalysisModalMode] = useState('edit');
  const [selected, setSelected] = useState(null);
  const [analysisForm, setAnalysisForm] = useState({ decision: '', rejection_reason: '', rejection_free_text: '', notes: '' });
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [returnRow, setReturnRow] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentRow, setAssignmentRow] = useState(null);
  const [selectedAnalyst, setSelectedAnalyst] = useState('');
  const [selectedForBulk, setSelectedForBulk] = useState(new Set());
  const [bulkAnalyst, setBulkAnalyst] = useState('');
  const [equitableSAnalysts, setEquitableSAnalysts] = useState(new Set());
  const [assignmentIsReassign, setAssignmentIsReassign] = useState(false);
  const [tableSort, setTableSort] = useState(() => initialAdminTableSort());
  const { addNotification } = useNotifications();
  const isAdminUser = (user?.role || '') === 'admin';

  const loadData = useCallback(async () => {
    const { id: processId } = parseAdminSelectedProcessId(selectedProcessId);
    if (processId == null) {
      setRows([]);
      setApplications([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      if (isAdminUser) {
        const [sarData, applicationsData, paymentsData, analystsData, usersData, reasonsData] = await Promise.all([
          apiClient.getSpecialAttendanceRequests(processId),
          apiClient.getApplications({ processId }),
          apiClient.getPayments(),
          apiClient.getUsers('analyst'),
          apiClient.getUsers(),
          apiClient.getRejectionReasons({ context: 'special_attendance' }),
        ]);
        const apps = Array.isArray(applicationsData) ? applicationsData : [];
        const paymentsList = Array.isArray(paymentsData) ? paymentsData : [];
        const analystsList = Array.isArray(analystsData) ? analystsData : [];
        const synced = apps.map((app) => {
          const payment = paymentsList.find(
            (p) => p.application_id === app.id || (p.user_id === (app.candidate_id || app.user_id) && p.process_id === app.process_id)
          );
          const appWithPayment = payment ? { ...app, payment_status: payment.status } : app;
          const analyst = analystsList.find((a) => a.id === appWithPayment.analyst_id);
          const analystName = analyst ? analyst.name || null : appWithPayment.analyst_name || null;
          return { ...appWithPayment, analyst_name: analystName };
        });
        setApplications(synced);
        setAnalysts(analystsList);
        setUsers(Array.isArray(usersData) ? usersData : []);
        setRejectionReasons(Array.isArray(reasonsData) ? reasonsData : []);
        const rawSar = Array.isArray(sarData) ? sarData : [];
        const appByIdForSar = new Map(synced.map((ap) => [ap.id, ap]));
        setRows(
          rawSar.map((row) => {
            const appRow = appByIdForSar.get(row.application_id);
            const mergedReturn =
              (row.return_reason && String(row.return_reason).trim()) ||
              (row.special_attendance_return_reason && String(row.special_attendance_return_reason).trim()) ||
              (appRow?.special_attendance_return_reason && String(appRow.special_attendance_return_reason).trim()) ||
              null;
            return mergedReturn ? { ...row, return_reason: mergedReturn } : { ...row };
          })
        );
      } else {
        const [sarData, reasonsData] = await Promise.all([
          apiClient.getSpecialAttendanceRequests(processId),
          apiClient.getRejectionReasons({ context: 'special_attendance' }).catch(() => []),
        ]);
        setRows(Array.isArray(sarData) ? sarData : []);
        setRejectionReasons(Array.isArray(reasonsData) ? reasonsData : []);
        setApplications([]);
      }
      setError('');
    } catch {
      setError('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  }, [selectedProcessId, isAdminUser]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSelectedForBulk(new Set());
    setBulkAnalyst('');
    setEquitableSAnalysts(new Set());
    setTableSort(initialAdminTableSort());
  }, [selectedProcessId]);

  useEffect(() => {
    if (!isAdminUser) return;
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          filterPayment,
          filterCandidate: String(filterCandidate || ''),
          filterAnalyst: String(filterAnalyst || ''),
          filterCourse: String(filterCourse || ''),
          filterCampus: String(filterCampus || ''),
          filterQuota: String(filterQuota || ''),
        })
      );
    } catch (e) {}
  }, [isAdminUser, filterPayment, filterCandidate, filterAnalyst, filterCourse, filterCampus, filterQuota]);

  useEffect(() => {
    if (isAdminUser) {
      setSelectedForBulk(new Set());
      setBulkAnalyst('');
      setFilterAnalyst('');
    }
  }, [selectedProcessId, isAdminUser]);

  const appById = useMemo(() => {
    const m = new Map();
    applications.forEach((a) => m.set(a.id, a));
    return m;
  }, [applications]);

  const analystsForProcess = useMemo(() => {
    const { empty, invalid, id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (empty) return analysts;
    if (invalid || pid == null) return [];
    return analysts.filter((a) => {
      const pids = a.process_ids || [];
      return pids.length === 0 || pids.includes(pid);
    });
  }, [analysts, selectedProcessId]);

  const analystFilterOptions = useMemo(() => {
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (pid == null) return [];
    const byId = new Map();
    analystsForProcess.forEach((a) => byId.set(String(a.id), { id: a.id, name: a.name || `Usuário #${a.id}` }));
    rows.forEach((r) => {
      if (Number(r.process_id) !== pid) return;
      const id = r.special_attendance_analyst_id;
      if (!id) return;
      const key = String(id);
      if (byId.has(key)) return;
      let name = r.special_attendance_analyst_name;
      if (!name) {
        const u = users.find((x) => Number(x.id) === Number(id));
        if (u) name = u.role === 'admin' ? u.name || 'Administrador' : u.name || `Usuário #${id}`;
        else name = `Usuário #${id}`;
      }
      byId.set(key, { id, name });
    });
    return Array.from(byId.values()).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
  }, [rows, selectedProcessId, analystsForProcess, users]);

  const courseFilterOptions = useMemo(() => {
    const names = new Set();
    rows.forEach((r) => {
      if (r.course_name) names.add(String(r.course_name));
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [rows]);

  const campusFilterOptions = useMemo(() => {
    const campuses = new Set();
    rows.forEach((r) => {
      if (r.course_campus) campuses.add(String(r.course_campus));
    });
    return Array.from(campuses).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [rows]);

  const quotaFilterOptions = useMemo(() => {
    const quotas = new Set();
    rows.forEach((r) => {
      quotas.add(String(r.quota_type || 'AC'));
    });
    return Array.from(quotas).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [rows]);

  const paymentForRow = useCallback(
    (r) => {
      const app = appById.get(r.application_id);
      return getPaymentStatusForAnalysis(app || null);
    },
    [appById]
  );

  const filtered = useMemo(() => {
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    return rows.filter((r) => {
      if (pid != null && r.process_id !== pid) return false;
      if (!isAdminUser) {
        if (filterStatus) {
          if (filterStatus === 'pending' && (r.status === 'pending' || r.status === 'analyzing')) return true;
          if (filterStatus && r.status !== filterStatus) return false;
        }
        return true;
      }
      const app = appById.get(r.application_id);
      if (filterPayment) {
        if (paymentForRow(r) !== filterPayment) return false;
      }
      if (filterStatus) {
        if (filterStatus === 'pending') {
          if (!(r.status === 'pending' || r.status === 'analyzing')) return false;
        } else if (filterStatus === 'returned') {
          if (!(sarReturnReasonText(r) && !r.special_attendance_analyst_id)) return false;
        } else if (r.status !== filterStatus) {
          return false;
        }
      }
      const q = (filterCandidate || '').trim().toLowerCase();
      if (q && ![r.candidate_name, r.candidate_cpf].some((v) => v && String(v).toLowerCase().includes(q))) return false;
      const aid = r.special_attendance_analyst_id;
      if (filterAnalyst && String(aid || '') !== String(filterAnalyst)) return false;
      if (filterCourse && String(r.course_name || '') !== String(filterCourse)) return false;
      if (filterCampus && String(r.course_campus || '') !== String(filterCampus)) return false;
      if (filterQuota && String(r.quota_type || 'AC') !== String(filterQuota)) return false;
      if (app?.excluded_at) return false;
      return true;
    });
  }, [
    rows,
    filterStatus,
    selectedProcessId,
    isAdminUser,
    filterPayment,
    filterCandidate,
    filterAnalyst,
    filterCourse,
    filterCampus,
    filterQuota,
    appById,
    paymentForRow,
  ]);

  const sortedRows = useMemo(
    () => sortSarRowsForDisplay(filtered, tableSort, appById),
    [filtered, tableSort, appById]
  );

  const pendingForBulk = useMemo(() => {
    if (!isAdminUser) return [];
    return filtered.filter((r) => {
      const ps = paymentForRow(r);
      const okPay = ps === 'paid' || ps === 'exempted' || ps === 'exemption_appeal_approved';
      const app = appById.get(r.application_id);
      if (app?.excluded_at) return false;
      return okPay && !r.special_attendance_analyst_id;
    });
  }, [filtered, isAdminUser, paymentForRow, appById]);

  const clearAdminFilters = useCallback(() => {
    setFilterPayment('');
    setFilterCandidate('');
    setFilterAnalyst('');
    setFilterCourse('');
    setFilterCampus('');
    setFilterQuota('');
    setFilterStatus('');
  }, []);

  const handleAccept = async (r) => {
    try {
      await apiClient.updateSpecialAttendanceRequest(r.id, { status: 'analyzing' });
      addNotification('Sucesso', 'Tarefa aceita', 'success');
      loadData();
    } catch {
      addNotification('Erro', 'Não foi possível aceitar', 'error');
    }
  };

  const openReturn = (r) => {
    setReturnRow(r);
    setReturnReason('');
    setShowReturnModal(true);
  };

  const submitReturn = async () => {
    if (!returnRow) return;
    try {
      await apiClient.updateApplication(returnRow.application_id, {
        analyst_id: null,
        special_attendance_return_reason: returnReason.trim() || 'Devolvido',
      });
      addNotification('Sucesso', 'Devolvido ao admin', 'success');
      setShowReturnModal(false);
      loadData();
    } catch {
      addNotification('Erro', 'Não foi possível devolver', 'error');
    }
  };

  const openAnalyze = (r) => {
    setAnalysisModalMode('edit');
    setSelected(r);
    const st = (r.status || '').toLowerCase();
    let rejId = '';
    let freeText = '';
    if (st === 'rejected' && r.rejection_reason) {
      const m = rejectionReasons.find(
        (x) => String(x.id) === String(r.rejection_reason) || (x.title && x.title === r.rejection_reason)
      );
      if (m) rejId = String(m.id);
      else freeText = r.rejection_reason;
    }
    setAnalysisForm({
      decision: st === 'rejected' ? 'rejected' : 'approved',
      rejection_reason: rejId,
      rejection_free_text: freeText,
      notes: '',
    });
    setShowModal(true);
  };

  const openView = (r) => {
    setAnalysisModalMode('view');
    setSelected(r);
    const st = (r.status || '').toLowerCase();
    let rejId = '';
    let freeText = '';
    if (st === 'rejected' && r.rejection_reason) {
      const m = rejectionReasons.find(
        (x) => String(x.id) === String(r.rejection_reason) || (x.title && x.title === r.rejection_reason)
      );
      if (m) rejId = String(m.id);
      else freeText = r.rejection_reason;
    }
    setAnalysisForm({
      decision: st === 'rejected' ? 'rejected' : 'approved',
      rejection_reason: rejId,
      rejection_free_text: freeText,
      notes: '',
    });
    setShowModal(true);
  };

  const saveAnalysis = async () => {
    if (!selected) return;
    let rejectionText = null;
    if (analysisForm.decision === 'rejected') {
      if (rejectionReasons.length > 0) {
        const obj = analysisForm.rejection_reason
          ? rejectionReasons.find((x) => String(x.id) === String(analysisForm.rejection_reason))
          : null;
        if (obj) {
          rejectionText = obj.title;
        } else if (analysisForm.rejection_free_text?.trim()) {
          rejectionText = analysisForm.rejection_free_text.trim();
        } else {
          addNotification('Campo obrigatório', 'Selecione um motivo de indeferimento', 'warning');
          return;
        }
      } else if (!analysisForm.rejection_free_text?.trim()) {
        addNotification('Campo obrigatório', 'Informe o motivo do indeferimento', 'warning');
        return;
      } else {
        rejectionText = analysisForm.rejection_free_text.trim();
      }
    }
    try {
      await apiClient.updateSpecialAttendanceRequest(selected.id, {
        status: analysisForm.decision,
        rejection_reason: analysisForm.decision === 'rejected' ? rejectionText : null,
      });
      addNotification('Sucesso', 'Decisão registrada', 'success');
      setShowModal(false);
      loadData();
    } catch {
      addNotification('Erro', 'Falha ao salvar', 'error');
    }
  };

  const downloadDoc = async (r, doc, index) => {
    try {
      const blob = await apiClient.downloadDocument({ specialAttendanceRequestId: r.id, index });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name || `doc_${index + 1}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      addNotification('Erro', e.message || 'Download', 'error');
    }
  };

  const openAssign = (r, preselectCurrent = false) => {
    setAssignmentRow(r);
    setAssignmentIsReassign(!!preselectCurrent);
    setSelectedAnalyst(preselectCurrent && r.special_attendance_analyst_id ? String(r.special_attendance_analyst_id) : '');
    setShowAssignmentModal(true);
  };

  const submitAssignment = async () => {
    if (!assignmentRow || !selectedAnalyst) {
      setError('Selecione um analista');
      return;
    }
    if (
      assignmentIsReassign &&
      Number(assignmentRow.special_attendance_analyst_id) === parseInt(selectedAnalyst, 10)
    ) {
      setError('O pedido já está atribuído a este analista');
      addNotification('Atenção', 'Selecione outro analista para reatribuir.', 'warning');
      return;
    }
    try {
      setLoading(true);
      const { error: updateError } = await apiClient.updateApplication(assignmentRow.application_id, {
        analyst_id: parseInt(selectedAnalyst, 10),
        assignment_type: 'special_attendance',
      });
      if (updateError) {
        setError(updateError);
        addNotification('Erro', updateError, 'error');
      } else {
        addNotification('Sucesso', 'Analista atribuído para atendimento especial', 'success');
        setShowAssignmentModal(false);
        setAssignmentRow(null);
        setAssignmentIsReassign(false);
        setSelectedAnalyst('');
        await loadData();
      }
    } catch {
      setError('Erro ao atribuir');
      addNotification('Erro', 'Erro ao atribuir', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkAnalyst || selectedForBulk.size === 0) {
      addNotification('Atenção', 'Selecione um analista e ao menos uma linha', 'warning');
      return;
    }
    const ids = Array.from(selectedForBulk);
    const appIds = ids
      .map((id) => filtered.find((x) => x.id === id))
      .filter((row) => row && isEligibleForSpecialAttendance(row))
      .map((row) => row.application_id);
    try {
      const { data, error } = await apiClient.bulkAssignSpecialAttendance(appIds, parseInt(bulkAnalyst, 10));
      if (error) throw new Error(error);
      const ok = data?.assigned_count ?? 0;
      addNotification('Atribuição em massa', `${ok} pedido(s) atribuído(s)`, 'success');
      setSelectedForBulk(new Set());
      setBulkAnalyst('');
      await loadData();
    } catch (e) {
      addNotification('Erro', e.message || 'Falha na atribuição em massa', 'error');
    }
  };

  const handleBulkAssignEquitable = async () => {
    if (selectedForBulk.size === 0 || equitableSAnalysts.size < 2) {
      addNotification('Atenção', 'Selecione pedidos e marque ao menos 2 analistas para distribuição equânime.', 'warning');
      return;
    }
    const ids = Array.from(selectedForBulk);
    const appIds = ids
      .map((id) => filtered.find((x) => x.id === id))
      .filter((row) => row && isEligibleForSpecialAttendance(row))
      .map((row) => row.application_id);
    const analystIds = Array.from(equitableSAnalysts)
      .map((x) => parseInt(x, 10))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);
    try {
      const { data, error } = await apiClient.bulkAssignSpecialAttendanceEquitable(appIds, analystIds);
      if (error) throw new Error(error);
      const ok = data?.assigned_count ?? 0;
      const per = data?.per_analyst || {};
      const parts = Object.entries(per)
        .filter(([, c]) => c > 0)
        .map(([id, c]) => {
          const an = analystsForProcess.find((x) => String(x.id) === String(id));
          return `${an?.name || id}: ${c}`;
        });
      addNotification(
        'Distribuição equânime',
        `${ok} pedido(s) atribuído(s)${parts.length ? ` — ${parts.join('; ')}` : ''}`,
        'success'
      );
      setSelectedForBulk(new Set());
      setEquitableSAnalysts(new Set());
      await loadData();
    } catch (e) {
      addNotification('Erro', e.message || 'Falha na distribuição equânime', 'error');
    }
  };

  const isEligibleForSpecialAttendance = useCallback(
    (r) => {
      const ps = paymentForRow(r);
      return ps === 'paid' || ps === 'exempted' || ps === 'exemption_appeal_approved';
    },
    [paymentForRow]
  );

  const canAdminAssign = (r) => {
    if (!isAdminUser || !isEligibleForSpecialAttendance(r)) return false;
    return !r.special_attendance_analyst_id && (r.status === 'pending' || r.status === 'analyzing' || sarDecisionBadgeKey(r) === 'returned');
  };

  const canAdminReassign = (r) => {
    if (!isAdminUser || !isEligibleForSpecialAttendance(r)) return false;
    if (sarReturnReasonText(r) && r.special_attendance_analyst_id) return false;
    if (!r.special_attendance_analyst_id) return false;
    const st = (r.status || '').toLowerCase();
    return st === 'pending' || st === 'analyzing';
  };

  const canAdminView = (r) => {
    if (!isAdminUser) return false;
    const st = (r.status || '').toLowerCase();
    return st === 'approved' || st === 'rejected';
  };

  const canAdminAnalyze = (r) => {
    if (!isAdminUser || !isEligibleForSpecialAttendance(r)) return false;
    const hasAssignedAnalyst = !!r.special_attendance_analyst_id;
    const st = (r.status || '').toLowerCase();
    const adminEditingOwnDecision =
      Number(r.special_attendance_analyst_id) === Number(user?.id) && (st === 'approved' || st === 'rejected');
    const adminDirectAnalyze = !hasAssignedAnalyst && (st === 'pending' || st === 'analyzing');
    return adminDirectAnalyze || adminEditingOwnDecision;
  };

  const canAdminFixStuckSar = (r) =>
    isAdminUser && !!(sarReturnReasonText(r) && r.special_attendance_analyst_id);

  const handleFixStuckSar = async (r) => {
    try {
      const { error } = await apiClient.resetSpecialAttendanceForReatribuicao(r.application_id);
      if (error) {
        addNotification('Erro', error, 'error');
        return;
      }
      addNotification('Sucesso', 'Corrigido. Reatribuição liberada.', 'success');
      await loadData();
    } catch (e) {
      addNotification('Erro', e.message || 'Falha ao corrigir', 'error');
    }
  };

  const hasModalOpen = showModal || showReturnModal || showAssignmentModal;

  useEffect(() => {
    if (!isAdminUser || hasModalOpen) return;
    const interval = setInterval(() => loadData(), 60 * 1000);
    return () => clearInterval(interval);
  }, [isAdminUser, hasModalOpen, loadData]);

  if (loading && rows.length === 0 && (!isAdminUser || applications.length === 0)) {
    return <LoadingSpinner text="Carregando atendimento especial..." />;
  }

  return (
    <div style={{ ...styles.contentContainer, padding: '16px' }}>
      <div style={{ ...styles.flexBetween, marginBottom: '12px' }}>
        <div>
          <h2 style={styles.cardTitle}>Atendimento especial</h2>
          <p style={{ fontSize: '14px', color: '#6B7280', margin: '4px 0 0 0' }}>
            Pedidos de atendimento especializado ou específico (processo por prova). A fila do analista só inclui inscrições pagas ou isentas.
            {isAdminUser && (
              <span>
                {' '}
                Atribua o analista aqui (mesmo critério da página Pagamentos / isenção: <code>assignment_type: special_attendance</code>) ou em massa abaixo.
              </span>
            )}
          </p>
        </div>
      </div>

      {isAdminUser && (
        <div style={{ ...styles.card, marginBottom: '16px' }}>
          <label style={styles.label}>Processo seletivo</label>
          <select value={selectedProcessId} onChange={setSelectedProcessId} style={{ ...styles.select, maxWidth: '400px' }}>
            <option value="">Selecione o processo</option>
            {processes.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && <Alert message={error} type="error" onClose={() => setError('')} />}

      {isAdminUser && (
        <>
          <div style={{ ...styles.card, marginBottom: '12px', padding: '14px 20px', backgroundColor: '#F9FAFB' }}>
            <div style={{ fontSize: '14px', color: '#374151', display: 'flex', flexWrap: 'wrap', gap: '12px 20px', alignItems: 'center' }}>
              <span>
                <strong>Legenda:</strong>
              </span>
              <span>
                {ACTION_ICONS.assign} Atribuir
              </span>
              <span>
                {ACTION_ICONS.reassign} Reatribuir
              </span>
              <span>
                {ACTION_ICONS.fix} Corrigir
              </span>
              <span>
                {ACTION_ICONS.view} Ver detalhes
              </span>
              <span>
                {ACTION_ICONS.analyze} Decidir (deferir / indeferir)
              </span>
              <span>✏️ Editar</span>
              <span>
                {ACTION_ICONS.copy} Documentos
              </span>
            </div>
          </div>

          <div style={{ ...styles.card, marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={styles.cardTitle}>Filtros</h3>
              <button
                type="button"
                onClick={clearAdminFilters}
                style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 12px', fontSize: '13px' }}
              >
                Limpar filtros
              </button>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Candidato</label>
                <input
                  type="text"
                  value={filterCandidate}
                  onChange={(e) => setFilterCandidate(e.target.value)}
                  placeholder="Nome, CPF…"
                  style={{ ...styles.select, fontFamily: 'inherit' }}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Analista (atend. esp.)</label>
                <select value={filterAnalyst} onChange={(e) => setFilterAnalyst(e.target.value)} style={styles.select}>
                  <option value="">Todos</option>
                  {analystFilterOptions.map((a) => (
                    <option key={a.id} value={String(a.id)}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Pagamento</label>
                <select value={filterPayment} onChange={(e) => setFilterPayment(e.target.value)} style={styles.select}>
                  <option value="">Todas as situações</option>
                  <option value="pending">Pendente</option>
                  <option value="pending_exemption_not_assigned">Isenção não atribuída</option>
                  <option value="pending_exemption_assigned">Isenção atribuída</option>
                  <option value="pending_exemption">Isenção em análise</option>
                  <option value="exemption_rejected">Isenção rejeitada</option>
                  <option value="exemption_returned">Isenção devolvida</option>
                  <option value="exemption_appealed">Recurso de isenção em análise</option>
                  <option value="exemption_appeal_approved">Recurso de isenção aprovado</option>
                  <option value="exemption_appeal_rejected">Recurso de isenção rejeitado</option>
                  <option value="paid">Pago</option>
                  <option value="exempted">Isento</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Decisão atend. esp.</label>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={styles.select}>
                  <option value="">Todas</option>
                  <option value="pending">Pendentes / em análise</option>
                  <option value="returned">Devolvida ao admin</option>
                  <option value="approved">Deferida / aprovada</option>
                  <option value="rejected">Indeferida / reprovada</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Curso</label>
                <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)} style={styles.select}>
                  <option value="">Todos</option>
                  {courseFilterOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Unidade</label>
                <select value={filterCampus} onChange={(e) => setFilterCampus(e.target.value)} style={styles.select}>
                  <option value="">Todas</option>
                  {campusFilterOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Cota</label>
                <select value={filterQuota} onChange={(e) => setFilterQuota(e.target.value)} style={styles.select}>
                  <option value="">Todas</option>
                  {quotaFilterOptions.map((q) => (
                    <option key={q} value={q}>
                      {q}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </>
      )}

      {!isAdminUser && (
        <div style={{ marginBottom: '12px' }}>
          <label style={styles.label}>Filtrar status</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={styles.select}>
            <option value="">Todos</option>
            <option value="pending">Pendentes / em análise</option>
            <option value="approved">Deferidos</option>
            <option value="rejected">Indeferidos</option>
          </select>
        </div>
      )}

      {isAdminUser && (
        <div style={{ ...styles.card, marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h3 style={styles.cardTitle}>Pedidos</h3>
              <span style={{ fontSize: '12px', color: '#6B7280' }}>
                Total: {filtered.length} | Pagas / isentas elegíveis:{' '}
                {filtered.filter((r) => ['paid', 'exempted', 'exemption_appeal_approved'].includes(paymentForRow(r))).length}
              </span>
            </div>
            {pendingForBulk.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 10, maxWidth: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setSelectedForBulk(new Set(pendingForBulk.map((x) => x.id)))}
                    style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 12 }}
                  >
                    Selecionar todos ({pendingForBulk.length})
                  </button>
                  <button type="button" onClick={() => setSelectedForBulk(new Set())} style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 12 }}>
                    Desmarcar
                  </button>
                  <select value={bulkAnalyst} onChange={(e) => setBulkAnalyst(e.target.value)} style={{ ...styles.select, minWidth: 180 }}>
                    <option value="">Atribuir a…</option>
                    {analystsForProcess.map((a) => (
                      <option key={a.id} value={String(a.id)}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleBulkAssign}
                    disabled={!bulkAnalyst || selectedForBulk.size === 0}
                    style={{ ...styles.button, ...styles.buttonPrimary, padding: '6px 12px', fontSize: 12 }}
                  >
                    Atribuir selecionados ({selectedForBulk.size})
                  </button>
                </div>
                <div style={{ paddingTop: 8, borderTop: '1px solid #E5E7EB' }}>
                  <div style={{ fontSize: 12, color: '#374151', fontWeight: 600, marginBottom: 4 }}>Distribuição equânime (rodízio)</div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>Marque 2 ou mais analistas.</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginBottom: 8 }}>
                    {analystsForProcess.map((a) => (
                      <label key={a.id} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={equitableSAnalysts.has(String(a.id))}
                          onChange={(e) => {
                            setEquitableSAnalysts((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(String(a.id));
                              else next.delete(String(a.id));
                              return next;
                            });
                          }}
                        />
                        {a.name}
                      </label>
                    ))}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => setEquitableSAnalysts(new Set(analystsForProcess.map((x) => String(x.id))))}
                      style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 11 }}
                    >
                      Marcar todos os analistas do processo
                    </button>
                    <button type="button" onClick={() => setEquitableSAnalysts(new Set())} style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 11 }}>
                      Limpar marcação
                    </button>
                    <button
                      type="button"
                      onClick={handleBulkAssignEquitable}
                      disabled={selectedForBulk.size === 0 || equitableSAnalysts.size < 2}
                      style={{
                        ...styles.button,
                        ...styles.buttonPrimary,
                        padding: '6px 12px',
                        fontSize: 12,
                        opacity: selectedForBulk.size === 0 || equitableSAnalysts.size < 2 ? 0.55 : 1,
                      }}
                    >
                      Distribuir ({selectedForBulk.size} pedidos)
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isAdminUser ? '10px' : '13px' }}>
          <thead>
            <tr style={{ background: '#F3F4F6' }}>
              {isAdminUser && pendingForBulk.length > 0 && (
                <th style={{ padding: 8, width: 36 }}>
                  <span title="Selecionar para atribuição em massa">☐</span>
                </th>
              )}
              {isAdminUser && (
                <th
                  role="columnheader"
                  aria-sort={tableSort.key === 'id' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                  style={{ padding: 8, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setTableSort((p) => nextAdminTableSort(p, 'id'))}
                  title="Ordenar por número da inscrição"
                >
                  Nº{sortColumnIndicator(tableSort, 'id')}
                </th>
              )}
              <th
                role="columnheader"
                aria-sort={tableSort.key === 'candidate' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                style={{ padding: 8, textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setTableSort((p) => nextAdminTableSort(p, 'candidate'))}
                title="Ordenar por nome do candidato"
              >
                Candidato{sortColumnIndicator(tableSort, 'candidate')}
              </th>
              <th style={{ padding: 8 }}>Curso</th>
              {isAdminUser && <th style={{ padding: 8 }}>Unidade</th>}
              {isAdminUser && <th style={{ padding: 8 }}>Cota</th>}
              {isAdminUser && <th style={{ ...styles.tableHeaderCell, minWidth: '90px', fontSize: '10px', padding: '8px 10px' }}>Pagamento</th>}
              <th style={{ ...styles.tableHeaderCell, minWidth: '90px', fontSize: '10px', padding: '8px 10px' }}>Situação</th>
              {isAdminUser && <th style={{ padding: 8 }}>Analista</th>}
              {!isAdminUser && <th style={{ padding: 8 }}>Efetivação</th>}
              <th style={{ padding: 8 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => (
              <tr key={r.id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                {isAdminUser && pendingForBulk.length > 0 && (
                  <td style={{ padding: 6, textAlign: 'center' }}>
                    {pendingForBulk.some((x) => x.id === r.id) ? (
                      <input
                        type="checkbox"
                        checked={selectedForBulk.has(r.id)}
                        onChange={(e) => {
                          setSelectedForBulk((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(r.id);
                            else next.delete(r.id);
                            return next;
                          });
                        }}
                      />
                    ) : null}
                  </td>
                )}
                {isAdminUser && (
                  <td style={{ padding: 8, fontWeight: 600, color: '#374151' }}>
                    #{r.application_id}
                  </td>
                )}
                <td style={{ padding: 8 }}>
                  {r.candidate_name}
                  <br />
                  <span style={{ color: '#6B7280' }}>{r.candidate_cpf}</span>
                </td>
                <td style={{ padding: 8 }}>{r.course_name}</td>
                {isAdminUser && <td style={{ padding: 8 }}>{r.course_campus || '—'}</td>}
                {isAdminUser && <td style={{ padding: 8, textAlign: 'center' }}>{r.quota_type || 'AC'}</td>}
                {isAdminUser && (
                  <td style={{ ...styles.tableCell, verticalAlign: 'middle', minWidth: '90px', whiteSpace: 'normal', padding: '8px 10px' }}>
                    <span
                      style={{
                        ...getStatusBadgeStyle(paymentForRow(r)),
                        ...styles.tableCellBadge,
                        fontSize: '9px',
                        padding: '4px 8px',
                      }}
                      title={getPaymentStatusLabel(paymentForRow(r))}
                    >
                      {getPaymentStatusLabel(paymentForRow(r))}
                    </span>
                  </td>
                )}
                <td style={{ ...styles.tableCell, verticalAlign: 'middle', minWidth: '90px', whiteSpace: 'normal', padding: '8px 10px' }}>
                  <span
                    style={{
                      ...getStatusBadgeStyle(sarDecisionBadgeKey(r)),
                      ...styles.tableCellBadge,
                      fontSize: '9px',
                      padding: '4px 8px',
                    }}
                  >
                    {sarDecisionLabel(r)}
                  </span>
                  {sarReturnReasonText(r) && !r.special_attendance_analyst_id && (
                    <div
                      style={{
                        marginTop: 6,
                        fontSize: '11px',
                        color: '#92400E',
                        lineHeight: 1.35,
                        maxWidth: 280,
                        whiteSpace: 'pre-wrap',
                      }}
                      title={sarReturnReasonText(r)}
                    >
                      <strong>Motivo devolução:</strong>{' '}
                      {sarReturnReasonText(r).length > 160 ? `${sarReturnReasonText(r).slice(0, 160)}…` : sarReturnReasonText(r)}
                    </div>
                  )}
                </td>
                {isAdminUser && (
                  <td style={{ padding: 8 }}>
                    <span
                      style={{
                        ...getStatusBadgeStyle(r.special_attendance_analyst_id ? 'assigned' : 'not_assigned'),
                        fontSize: '9px',
                        padding: '4px 8px',
                        textTransform: 'none',
                        letterSpacing: 0,
                      }}
                      title={r.special_attendance_analyst_name || getAssignmentStatusLabel('not_assigned')}
                    >
                      {r.special_attendance_analyst_name || getAssignmentStatusLabel('not_assigned')}
                    </span>
                  </td>
                )}
                {!isAdminUser && (
                  <td style={{ padding: 8 }}>
                    {r.eligible_for_queue ? (
                      <span style={{ color: '#059669' }}>OK</span>
                    ) : (
                      <span style={{ color: '#B45309' }}>Aguardando pagamento ou isenção</span>
                    )}
                  </td>
                )}
                <td style={{ ...styles.tableCell, whiteSpace: 'normal', fontSize: isAdminUser ? '10px' : '13px', padding: '8px 10px' }}>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {isAdminUser && !isEligibleForSpecialAttendance(r) && (
                      <span style={{ fontSize: '11px', color: '#6B7280' }}>Aguardando pagamento ou isenção</span>
                    )}
                    {isAdminUser && canAdminAssign(r) && (
                      <button
                        type="button"
                        style={{ ...getButtonStyle('assignment', 'small'), padding: '3px 5px', fontSize: '9px' }}
                        onClick={() => openAssign(r)}
                      >
                        {ACTION_ICONS.assign} Atribuir
                      </button>
                    )}
                    {isAdminUser &&
                      (() => {
                        if (canAdminAssign(r)) return null;
                        if (canAdminFixStuckSar(r)) {
                          return (
                            <button
                              type="button"
                              onClick={() => handleFixStuckSar(r)}
                              style={{
                                ...getButtonStyle('secondary', 'small'),
                                padding: '3px 5px',
                                fontSize: '9px',
                                backgroundColor: '#F59E0B',
                                color: 'white',
                                border: 'none',
                              }}
                              title="Corrigir estado inconsistente (devolução com analista ainda atribuído)"
                            >
                              {ACTION_ICONS.fix} Corrigir
                            </button>
                          );
                        }
                        return null;
                      })()}
                    {isAdminUser && canAdminReassign(r) && (
                      <button
                        type="button"
                        style={{ ...getButtonStyle('secondary', 'small'), padding: '3px 6px', fontSize: '9px' }}
                        onClick={() => openAssign(r, true)}
                      >
                        {ACTION_ICONS.reassign} Reatribuir
                      </button>
                    )}
                    {isAdminUser && canAdminView(r) && (
                      <button
                        type="button"
                        title="Ver detalhes"
                        style={{
                          padding: '3px 6px',
                          border: 'none',
                          borderRadius: '4px',
                          backgroundColor: '#DBEAFE',
                          color: '#1D4ED8',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                        onClick={() => openView(r)}
                      >
                        {ACTION_ICONS.view}
                      </button>
                    )}
                    {isAdminUser && canAdminAnalyze(r) && (
                      <button type="button" style={{ ...getButtonStyle('primary', 'small'), fontSize: '10px' }} onClick={() => openAnalyze(r)}>
                        {Number(r.special_attendance_analyst_id) === Number(user?.id) && (r.status === 'approved' || r.status === 'rejected')
                          ? '✏️ Editar'
                          : `${ACTION_ICONS.analyze} Decidir`}
                      </button>
                    )}
                    {!isAdminUser && r.special_attendance_analyst_id === user?.id && r.status === 'pending' && (
                      <>
                        <button type="button" style={{ ...getButtonStyle('primary', 'small') }} onClick={() => handleAccept(r)}>
                          ✅ Aceitar
                        </button>
                        <button type="button" style={getButtonStyle('secondary', 'small')} onClick={() => openReturn(r)}>
                          ↩️ Devolver
                        </button>
                        <button
                          type="button"
                          disabled
                          style={{ ...getButtonStyle('secondary', 'small'), opacity: 0.5, cursor: 'not-allowed' }}
                          title="Aceite a tarefa para analisar"
                        >
                          {ACTION_ICONS.analyze}
                        </button>
                      </>
                    )}
                    {!isAdminUser && r.special_attendance_analyst_id === user?.id && r.status === 'analyzing' && (
                      <>
                        <button type="button" style={getButtonStyle('secondary', 'small')} onClick={() => openReturn(r)}>
                          ↩️ Devolver
                        </button>
                        <button type="button" style={getButtonStyle('primary', 'small')} onClick={() => openAnalyze(r)}>
                          {ACTION_ICONS.analyze}
                        </button>
                      </>
                    )}
                    {!isAdminUser &&
                      r.special_attendance_analyst_id === user?.id &&
                      (r.status === 'approved' || r.status === 'rejected') && (
                        <button type="button" style={getButtonStyle('secondary', 'small')} onClick={() => openAnalyze(r)}>
                          {ACTION_ICONS.edit} Editar
                        </button>
                      )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p style={{ marginTop: 12, color: '#6B7280' }}>Nenhum pedido neste processo.</p>}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={
          analysisModalMode === 'view'
            ? `${ACTION_ICONS.view} Detalhes da Inscrição e Atendimento Especial`
            : `🎫 Análise de Atendimento Especial - ${selected?.candidate_name || ''}`
        }
      >
        {selected && (
          <div>
            {analysisModalMode === 'view' ? (
              <>
                <div style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6, marginBottom: '20px' }}>
                  <p><strong>Inscrição Nº:</strong> #{selected.application_id}</p>
                  <p><strong>Candidato:</strong> {selected.candidate_name || 'N/A'}</p>
                  <p><strong>CPF:</strong> {selected.candidate_cpf || 'N/A'}</p>
                  <p><strong>Curso:</strong> {selected.course_name || 'N/A'}</p>
                  <p><strong>Unidade:</strong> {selected.course_campus || '—'}</p>
                  <p><strong>Analista Atendimento Especial:</strong> {selected.special_attendance_analyst_name || 'Não atribuído'}</p>
                  <p><strong>Situação Pagamento:</strong> {getPaymentStatusLabel(paymentForRow(selected))}</p>
                </div>

                <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#FEF3C7', borderRadius: '8px', border: '1px solid #F59E0B' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#92400E' }}>🎫 Pedido de Atendimento Especial</h4>
                  <p style={{ margin: '0 0 8px 0', fontSize: '13px' }}>
                    <strong>Status:</strong>{' '}
                    {(() => {
                      const lbl = getSpecialAttendanceStatusLabel(selected.status || 'pending');
                      const st = (selected.status || '').toLowerCase();
                      if (st === 'approved') return `✅ ${lbl}`;
                      if (st === 'rejected') return `❌ ${lbl}`;
                      if (st === 'analyzing') return `📝 ${lbl}`;
                      if (st === 'appealed') return `⏳ ${lbl}`;
                      return `⏳ ${lbl}`;
                    })()}
                  </p>
                  {sarReturnReasonText(selected) && !selected.special_attendance_analyst_id && (
                    <p style={{ margin: '8px 0 0 0', fontSize: '13px', whiteSpace: 'pre-wrap', color: '#92400E' }}>
                      <strong>Motivo da devolução (analista):</strong> {sarReturnReasonText(selected)}
                    </p>
                  )}
                  {selected.rejection_reason && (
                    <p style={{ margin: '8px 0 0 0', fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                      <strong>Motivo:</strong> {selected.rejection_reason}
                    </p>
                  )}
                  {(selected.documents || []).length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <strong style={{ fontSize: '13px' }}>Documentos:</strong>
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {(selected.documents || []).map((doc, idx) => (
                          <div key={idx} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '6px 8px',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            border: '1px solid #E5E7EB'
                          }}>
                            <span style={{ fontSize: '12px', color: '#374151' }}>
                              {(doc && doc.name) || `Documento ${idx + 1}`}
                            </span>
                            <button
                              type="button"
                              onClick={() => downloadDoc(selected, doc || {}, idx)}
                              style={{ ...styles.button, ...styles.buttonSecondary, padding: '4px 8px', fontSize: '11px' }}
                            >
                              Baixar
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => setShowModal(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>
                    Fechar
                  </button>
                </div>
              </>
            ) : (
              <>
                {sarReturnReasonText(selected) && !selected.special_attendance_analyst_id && (
                  <div
                    style={{
                      marginBottom: '16px',
                      padding: '12px',
                      background: '#FEF3C7',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: '#92400E',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    <strong>Motivo da devolução (analista):</strong>
                    <div style={{ marginTop: 6 }}>{sarReturnReasonText(selected)}</div>
                  </div>
                )}
                <div style={{ marginBottom: '20px' }}>
                  <label style={styles.label}>📁 Documento Comprobatório</label>
                  <div
                    style={{
                      backgroundColor: '#F9FAFB',
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid #E5E7EB',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>
                      {(selected.documents && selected.documents[0] && selected.documents[0].name) || 'documento_atendimento_especial.pdf'}
                    </span>
                    <button
                      type="button"
                      onClick={() => downloadDoc(selected, selected.documents?.[0] || {}, 0)}
                      disabled={!selected.documents || selected.documents.length === 0}
                      style={{
                        ...styles.button,
                        ...styles.buttonSecondary,
                        fontSize: '12px',
                        padding: '6px 12px',
                        opacity: !selected.documents || selected.documents.length === 0 ? 0.6 : 1,
                      }}
                    >
                      📄 Download
                    </button>
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={styles.label}>⚖️ Situação *</label>
                  <select
                    value={analysisForm.decision}
                    onChange={(e) => setAnalysisForm((f) => ({ ...f, decision: e.target.value }))}
                    style={styles.select}
                  >
                    <option value="approved">Deferido</option>
                    <option value="rejected">Indeferido</option>
                  </select>
                </div>

                {analysisForm.decision === 'rejected' && (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={styles.label}>❌ Motivo do Indeferimento *</label>
                    {rejectionReasons.length > 0 ? (
                      <>
                        <select
                          value={analysisForm.rejection_reason}
                          onChange={(e) => setAnalysisForm((f) => ({ ...f, rejection_reason: e.target.value }))}
                          style={styles.select}
                        >
                          <option value="">Selecione um motivo</option>
                          {rejectionReasons.map((reason) => (
                            <option key={reason.id} value={String(reason.id)}>
                              {reason.title}
                            </option>
                          ))}
                        </select>
                        <textarea
                          value={analysisForm.rejection_free_text}
                          onChange={(e) => setAnalysisForm((f) => ({ ...f, rejection_free_text: e.target.value }))}
                          style={{ ...styles.textarea, marginTop: '8px' }}
                          placeholder="Complemento (opcional)"
                          rows="2"
                        />
                      </>
                    ) : (
                      <textarea
                        value={analysisForm.rejection_free_text}
                        onChange={(e) => setAnalysisForm((f) => ({ ...f, rejection_free_text: e.target.value }))}
                        style={styles.textarea}
                        placeholder="Informe o motivo do indeferimento"
                        rows="3"
                      />
                    )}
                  </div>
                )}

                <div style={{ marginBottom: '24px' }}>
                  <label style={styles.label}>📝 Observações Gerais</label>
                  <textarea
                    value={analysisForm.notes}
                    onChange={(e) => setAnalysisForm((f) => ({ ...f, notes: e.target.value }))}
                    style={styles.textarea}
                    placeholder="Observações sobre a análise do pedido de atendimento especial..."
                    rows="3"
                  />
                </div>

                <div style={{ ...styles.flex, justifyContent: 'flex-end', ...styles.gap4 }}>
                  <button type="button" onClick={() => setShowModal(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>
                    ❌ Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={saveAnalysis}
                    disabled={
                      (analysisForm.decision !== 'approved' && analysisForm.decision !== 'rejected') ||
                      (analysisForm.decision === 'rejected' &&
                        !analysisForm.rejection_reason &&
                        !(analysisForm.rejection_free_text || '').trim())
                    }
                    style={{
                      ...styles.button,
                      ...((analysisForm.decision !== 'approved' && analysisForm.decision !== 'rejected') ||
                      (analysisForm.decision === 'rejected' &&
                        !analysisForm.rejection_reason &&
                        !(analysisForm.rejection_free_text || '').trim())
                        ? styles.buttonDisabled
                        : styles.buttonPrimary),
                    }}
                  >
                    💾 Finalizar Análise
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>

      <Modal isOpen={showReturnModal} onClose={() => setShowReturnModal(false)} title="Devolver tarefa">
        <textarea
          value={returnReason}
          onChange={(e) => setReturnReason(e.target.value)}
          style={{ ...styles.input, minHeight: 80, width: '100%' }}
          placeholder="Motivo"
        />
        <button type="button" style={{ ...styles.button, ...styles.buttonPrimary, marginTop: 12 }} onClick={submitReturn}>
          Confirmar
        </button>
      </Modal>

      <Modal
        isOpen={showAssignmentModal}
        onClose={() => {
          setShowAssignmentModal(false);
          setAssignmentRow(null);
          setAssignmentIsReassign(false);
          setSelectedAnalyst('');
        }}
        title={assignmentIsReassign ? 'Reatribuir — atendimento especial' : 'Atribuir analista — atendimento especial'}
      >
        <p style={{ fontSize: '13px', color: '#4B5563' }}>
          Inscrição #{assignmentRow?.application_id} — {assignmentRow?.candidate_name}
        </p>
        {(assignmentRow?.return_reason || assignmentRow?.special_attendance_return_reason) ? (
          <div
            style={{
              marginBottom: 12,
              padding: '10px 12px',
              background: '#FEF3C7',
              borderRadius: 8,
              fontSize: '13px',
              color: '#92400E',
            }}
          >
            <strong>Motivo da devolução (analista):</strong>
            <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>
              {assignmentRow.return_reason || assignmentRow.special_attendance_return_reason}
            </div>
          </div>
        ) : null}
        <label style={styles.label}>Analista</label>
        <select value={selectedAnalyst} onChange={(e) => setSelectedAnalyst(e.target.value)} style={{ ...styles.select, width: '100%' }}>
          <option value="">Selecione…</option>
          {analystsForProcess.map((a) => (
            <option key={a.id} value={String(a.id)}>
              {a.name}
            </option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button type="button" style={{ ...styles.button, ...styles.buttonPrimary }} onClick={submitAssignment}>
            Confirmar
          </button>
          <button
            type="button"
            style={{ ...styles.button, ...styles.buttonSecondary }}
            onClick={() => {
              setShowAssignmentModal(false);
              setAssignmentRow(null);
              setAssignmentIsReassign(false);
              setSelectedAnalyst('');
            }}
          >
            Cancelar
          </button>
        </div>
      </Modal>
    </div>
  );
});
